import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { formatPrice } from '../../utils/priceFormatter';
import BottomNav from '../../components/layouts/BottomNav';
import { FaArrowLeft, FaEye, FaBox, FaTruck, FaCheckCircle } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";

const ManageOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchUserOrders();
  }, []);

  const fetchUserOrders = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      if (!token || !user.id) {
        // Guest user - don't show error, just empty list or could show a dedicated component
        setOrders([]);
        setIsLoading(false);
        return;
      }

      const { data } = await api.get(`/orders/user/${user.id}`);
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setIsModalOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return { bg: '#fef3c7', text: '#92400e', icon: <FaBox size={16} /> };
      case 'confirmed': return { bg: '#dbeafe', text: '#1e40af', icon: <FaBox size={16} /> };
      case 'processing': return { bg: '#e9d5ff', text: '#7c3aed', icon: <FaBox size={16} /> };
      case 'shipped': return { bg: '#e0e7ff', text: '#3730a3', icon: <FaTruck size={16} /> };
      case 'delivered': return { bg: '#d1fae5', text: '#065f46', icon: <FaCheckCircle size={16} /> };
      case 'cancelled': return { bg: '#fee2e2', text: '#991b1b', icon: <FaBox size={16} /> };
      default: return { bg: '#f3f4f6', text: '#374151', icon: <FaBox size={16} /> };
    }
  };

  const getStatusText = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div style={{ background: "#fafbfc", minHeight: "100vh", paddingBottom: 74 }}>
        <div style={{
          display: "flex", alignItems: "center", padding: "18px 18px 10px 18px",
          borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none", border: "none", fontSize: 26, color: "#222",
              cursor: "pointer", lineHeight: 1, marginRight: 14
            }}
          >
            &larr;
          </button>
          <span style={{ fontWeight: 700, fontSize: 22, color: "#232323" }}>
            My Orders
          </span>
        </div>

        <div style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          height: "50vh", flexDirection: "column"
        }}>
          <div style={{
            width: 40, height: 40, border: "3px solid #f3f3f3",
            borderTop: "3px solid #333", borderRadius: "50%", animation: "spin 1s linear infinite"
          }}></div>
          <p style={{ marginTop: 20, color: "#666" }}>Loading your orders...</p>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ background: "#fafbfc", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", padding: "18px 18px 10px 18px",
        borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none", border: "none", fontSize: 26, color: "#222",
            cursor: "pointer", lineHeight: 1, marginRight: 14
          }}
        >
          &larr;
        </button>
        <span style={{ fontWeight: 700, fontSize: 22, color: "#232323" }}>
          My Orders
        </span>
      </div>

      {/* Orders List */}
      <div style={{ padding: "16px" }}>
        {orders.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", padding: "60px 20px", textAlign: "center"
          }}>
            <div style={{ fontSize: 64, marginBottom: 20, color: "#ddd" }}>
              📦
            </div>

            {/* Check if user is logged in */}
            {!JSON.parse(localStorage.getItem('user') || '{}').id ? (
              <>
                <h2 style={{
                  fontSize: 24, fontWeight: 600, color: "#333", marginBottom: 12
                }}>
                  Login Required
                </h2>
                <p style={{
                  fontSize: 16, color: "#666", marginBottom: 24, lineHeight: 1.5
                }}>
                  Please login to view your order history
                </p>
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    background: "#000", color: "#fff", border: "none",
                    padding: "12px 24px", borderRadius: 8, fontSize: 16,
                    fontWeight: 600, cursor: "pointer"
                  }}
                >
                  Login Now
                </button>
              </>
            ) : (
              <>
                <h2 style={{
                  fontSize: 24, fontWeight: 600, color: "#333", marginBottom: 12
                }}>
                  No Orders Yet
                </h2>
                <p style={{
                  fontSize: 16, color: "#666", marginBottom: 24, lineHeight: 1.5
                }}>
                  You haven't placed any orders yet. Start shopping to see your order history here.
                </p>
                <button
                  onClick={() => navigate('/categories')}
                  style={{
                    background: "#333", color: "#fff", border: "none",
                    padding: "12px 24px", borderRadius: 8, fontSize: 16,
                    fontWeight: 600, cursor: "pointer"
                  }}
                >
                  Start Shopping
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {orders.map((order) => {
              const statusStyle = getStatusColor(order.status);
              return (
                <div
                  key={order.id}
                  style={{
                    background: "#fff", borderRadius: 12, padding: 16,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)", cursor: "pointer"
                  }}
                  onClick={() => openOrderModal(order)}
                >
                  {/* Order Header */}
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12
                  }}>
                    <div>
                      <div style={{
                        fontWeight: 600, fontSize: 16, color: "#333", marginBottom: 4
                      }}>
                        Order #{order.order_number}
                      </div>
                      <div style={{ fontSize: 14, color: "#666" }}>
                        {formatDate(order.createdAt)}
                      </div>
                    </div>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 20,
                      background: statusStyle.bg, color: statusStyle.text,
                      fontSize: 12, fontWeight: 600
                    }}>
                      {statusStyle.icon}
                      {getStatusText(order.status)}
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12
                  }}>
                    <div style={{ fontSize: 14, color: "#666" }}>
                      {order.items?.length || 0} items
                    </div>
                    <div style={{
                      fontWeight: 600, fontSize: 16, color: "#333"
                    }}>
                      {formatPrice(order.total_amount)}
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div style={{
                    fontSize: 14, color: "#666", textTransform: "uppercase"
                  }}>
                    {order.payment_method}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {isModalOpen && selectedOrder && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000,
          padding: 16
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, maxWidth: 500, width: "100%",
            maxHeight: "90vh", overflow: "auto"
          }}>
            {/* Modal Header */}
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", padding: 20, borderBottom: "1px solid #eee"
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 600, color: "#333" }}>
                Order #{selectedOrder.order_number}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: "none", border: "none", fontSize: 24,
                  color: "#666", cursor: "pointer"
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: 20 }}>
              {/* Order Status */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  Order Status
                </h3>
                {(() => {
                  const statusStyle = getStatusColor(selectedOrder.status);
                  return (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "8px 16px", borderRadius: 8,
                      background: statusStyle.bg, color: statusStyle.text,
                      fontSize: 14, fontWeight: 600, width: "fit-content"
                    }}>
                      {statusStyle.icon}
                      {getStatusText(selectedOrder.status)}
                    </div>
                  );
                })()}
              </div>

              {/* Order Details */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  Order Details
                </h3>
                <div style={{ fontSize: 14, color: "#666", lineHeight: 1.6 }}>
                  <div><strong>Date:</strong> {formatDate(selectedOrder.createdAt)}</div>
                  <div><strong>Payment Method:</strong> {selectedOrder.payment_method.toUpperCase()}</div>
                  <div><strong>Total Amount:</strong> {formatPrice(selectedOrder.total_amount)}</div>
                  {selectedOrder.tracking_number && (
                    <div><strong>Tracking Number:</strong> {selectedOrder.tracking_number}</div>
                  )}
                </div>
              </div>

              {/* Shipping Address */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                  Shipping Address
                </h3>
                <div style={{
                  fontSize: 14, color: "#666", lineHeight: 1.6,
                  whiteSpace: "pre-line"
                }}>
                  {selectedOrder.shipping_address}
                </div>
              </div>

              {/* Order Items */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                  Order Items
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {selectedOrder.items?.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex", gap: 12, padding: 12,
                        border: "1px solid #eee", borderRadius: 8
                      }}
                    >
                      <div style={{
                        width: 60, height: 60, background: "#f5f5f5",
                        borderRadius: 6, overflow: "hidden", flexShrink: 0
                      }}>
                        {item.product_image ? (
                          <img
                            src={item.product_image.startsWith('http')
                              ? item.product_image
                              : `${API_BASE}item.product_image`}
                            alt={item.product_name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <div style={{
                            width: "100%", height: "100%",
                            display: "flex", alignItems: "center",
                            justifyContent: "center", color: "#999", fontSize: 12
                          }}>
                            No Img
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: 600, fontSize: 14, color: "#333",
                          marginBottom: 4
                        }}>
                          {item.product_name}
                        </div>
                        <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
                          Qty: {item.quantity}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                          {formatPrice(item.total_price)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Close Button */}
              <div style={{ textAlign: "center" }}>
                <button
                  onClick={closeModal}
                  style={{
                    background: "#333", color: "#fff", border: "none",
                    padding: "12px 24px", borderRadius: 8, fontSize: 16,
                    fontWeight: 600, cursor: "pointer"
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ManageOrdersPage;
